import { Command } from "commander";
import { readFileSync, existsSync, writeFileSync, mkdirSync } from "node:fs";
import { resolve } from "node:path";
import chalk from "chalk";
import Table from "cli-table3";
import {
  parseSessionFile,
  findSessionFiles,
  autoTag,
  readStatsCache,
  applyStatsAttribution,
} from "@fathom/tracker";
import type { Registry, NormalizedSession, TagResult } from "@fathom/tracker";
import { confirm } from "@inquirer/prompts";
import { syncActual, isConvexConnected, logConvexStatus } from "../convex-client.js";
import { getProjectName } from "../project-name.js";
import { withSpinner } from "../ux/spinner.js";
import { fatalError } from "../ux/errors.js";

export const trackCommand = new Command("track")
  .description("Import Claude Code sessions and auto-tag to features")
  .option("-p, --project <name>", "Project name")
  .option("--registry <path>", "Path to registry.json")
  .option("--sessions-dir <path>", "Path to sessions directory")
  .option("--backfill", "Process all existing sessions (not just new)")
  .option("--dry-run", "Show what would change without modifying files")
  .option("-y, --yes", "Skip confirmation prompt")
  .action(
    async (options: {
      project?: string;
      registry?: string;
      sessionsDir?: string;
      backfill?: boolean;
      dryRun?: boolean;
      yes?: boolean;
    }) => {
    try {
      const registryPath =
        options.registry ??
        resolve(process.cwd(), ".fathom", "registry.json");

      if (!existsSync(registryPath)) {
        console.log(
          chalk.yellow(
            "No registry found. Run `fathom analyze <spec> --project <name>` first.",
          ),
        );
        return;
      }

      let registry: Registry;
      try {
        registry = JSON.parse(readFileSync(registryPath, "utf-8"));
      } catch {
        fatalError("Invalid registry", "Failed to parse " + registryPath, "Delete and re-run fathom analyze");
      }
      const projectName = options.project ?? registry.project ?? getProjectName();

      console.log(chalk.dim("Scanning for sessions..."));

      const sessionFiles = findSessionFiles(options.sessionsDir);

      if (sessionFiles.length === 0) {
        console.log(
          chalk.yellow("No session files found. Run some Claude Code sessions first."),
        );
        return;
      }

      // Load already-tracked sessions to skip duplicates
      const trackingDir = resolve(
        process.cwd(),
        ".fathom",
        "tracking",
        "sessions",
      );
      const summaryPath = resolve(
        process.cwd(),
        ".fathom",
        "tracking",
        "summary.json",
      );
      const trackedIds = new Set<string>();

      if (!options.backfill && existsSync(summaryPath)) {
        let summary: { sessions?: Array<{ sessionId: string }> };
        try {
          summary = JSON.parse(readFileSync(summaryPath, "utf-8"));
        } catch {
          fatalError("Invalid tracking data", "Failed to parse " + summaryPath, "Delete and re-run fathom track --backfill");
        }
        for (const s of summary.sessions ?? []) {
          trackedIds.add(s.sessionId);
        }
      }

      // Parse and tag sessions
      const results: Array<{
        session: NormalizedSession;
        tag: TagResult;
      }> = [];

      for (const file of sessionFiles) {
        const session = parseSessionFile(file);
        if (!session) continue;
        if (trackedIds.has(session.sessionId)) continue;

        const tag = autoTag(session, registry.features);
        results.push({ session, tag });
      }

      if (results.length === 0) {
        console.log(
          chalk.dim("No new sessions to process."),
        );
        return;
      }

      // Apply stats-cache attribution for sessions missing API usage
      const statsCache = readStatsCache();
      let attributionSummary: string | null = null;
      if (statsCache) {
        const allSessions = results.map((r) => r.session);
        const attribution = applyStatsAttribution(allSessions, statsCache);
        if (attribution.upgraded > 0) {
          attributionSummary =
            `Stats-cache: ${attribution.upgraded} sessions upgraded, ` +
            `${attribution.unchanged} had API data, ` +
            `${attribution.unattributed} unattributed`;
        }
      }

      // Display results
      console.log(
        chalk.bold(`\nFathom — Track: ${projectName} (${results.length} sessions)`),
      );
      console.log(chalk.dim("─".repeat(65)));

      const table = new Table({
        head: [
          chalk.white("Session"),
          chalk.white("Feature"),
          chalk.white("Confidence"),
          chalk.white("Tag"),
          chalk.white("Tokens"),
          chalk.white("Token Src"),
        ],
        colAligns: ["left", "left", "center", "center", "right", "center"],
      });

      let totalTokens = 0;
      let autoTagged = 0;
      let likely = 0;
      let untagged = 0;

      for (const { session, tag } of results) {
        const tokens = session.inputTokens + session.outputTokens;
        totalTokens += tokens;

        const featureLabel = tag.featureId
          ? registry.features.find((f) => f.id === tag.featureId)?.name ??
            tag.featureId
          : chalk.dim("—");

        const confidenceColor =
          tag.status === "auto-tagged"
            ? chalk.green
            : tag.status === "likely"
              ? chalk.yellow
              : chalk.dim;

        if (tag.status === "auto-tagged") autoTagged++;
        else if (tag.status === "likely") likely++;
        else untagged++;

        const srcColor =
          session.tokenSource === "api"
            ? chalk.green
            : session.tokenSource === "stats-cache"
              ? chalk.cyan
              : session.tokenSource === "tokenizer"
                ? chalk.yellow
                : chalk.dim;

        table.push([
          session.sessionId.slice(0, 12) + "…",
          featureLabel,
          confidenceColor(`${(tag.confidence * 100).toFixed(0)}%`),
          tag.source,
          tokens.toLocaleString(),
          srcColor(session.tokenSource),
        ]);
      }

      console.log(table.toString());

      console.log(
        chalk.dim(
          `\nTotal: ${totalTokens.toLocaleString()} tokens across ${results.length} sessions`,
        ),
      );
      console.log(
        chalk.dim(
          `Tags: ${autoTagged} auto-tagged, ${likely} likely, ${untagged} untagged`,
        ),
      );

      if (attributionSummary) {
        console.log(chalk.cyan(`  ${attributionSummary}`));
      }

      // Dry-run: show what would happen, then exit
      if (options.dryRun) {
        console.log(chalk.dim(`\n[dry-run] Would write ${results.length} sessions to tracking. No files modified.`));
        return;
      }

      // Confirmation prompt (skip with --yes)
      if (!options.yes) {
        const proceed = await confirm({
          message: `Track ${results.length} sessions and update summary? (use --yes to skip)`,
          default: true,
        });
        if (!proceed) {
          console.log(chalk.dim("\n  Aborted."));
          return;
        }
      }

      // Persist tracking data
      mkdirSync(trackingDir, { recursive: true });

      const sessionRecords = results.map(({ session, tag }) => ({
        sessionId: session.sessionId,
        featureId: tag.featureId,
        confidence: tag.confidence,
        source: tag.source,
        status: tag.status,
        inputTokens: session.inputTokens,
        outputTokens: session.outputTokens,
        cacheReadTokens: session.cacheReadTokens,
        model: session.model,
        sessionStart: session.sessionStart,
        sessionEnd: session.sessionEnd,
        activeMinutes: session.activeMinutes,
        tokenSource: session.tokenSource,
        trackedAt: new Date().toISOString(),
      }));

      // Merge with existing summary
      let existing: { sessions: typeof sessionRecords } = { sessions: [] };
      if (existsSync(summaryPath)) {
        try {
          existing = JSON.parse(readFileSync(summaryPath, "utf-8"));
        } catch {
          fatalError("Invalid tracking data", "Failed to parse " + summaryPath, "Delete and re-run fathom track --backfill");
        }
      }

      existing.sessions.push(...sessionRecords);

      writeFileSync(
        summaryPath,
        JSON.stringify(
          {
            project: projectName,
            sessions: existing.sessions,
            lastUpdated: new Date().toISOString(),
          },
          null,
          2,
        ),
      );

      console.log(chalk.dim(`\nTracking saved: ${summaryPath}`));

      // Sync to Convex
      if (isConvexConnected()) {
        const syncCount = await withSpinner("Syncing to Convex...", async () => {
          let count = 0;
          for (const { session, tag } of results) {
            const synced = await syncActual({
              projectName,
              sessionId: session.sessionId,
              provider: "claude",
              model: session.model,
              inputTokens: session.inputTokens,
              outputTokens: session.outputTokens,
              cacheReadTokens: session.cacheReadTokens,
              cacheCreateTokens: 0,
              toolCalls: 0,
              estimatedCost: 0,
              sessionStart: session.sessionStart,
              sessionEnd: session.sessionEnd,
              activeMinutes: session.activeMinutes,
              featureId: tag.featureId,
              tagConfidence: tag.confidence,
              tagSource: tag.source,
              tagStatus: tag.status,
            });
            if (synced) count++;
          }
          return count;
        });
        logConvexStatus(syncCount > 0);
        if (syncCount > 0) {
          console.log(chalk.dim(`  ${syncCount} sessions synced to Convex.`));
        }
      } else {
        logConvexStatus(false);
      }
    } catch (err: unknown) {
      if (err && typeof err === "object" && "name" in err && (err as Error).name === "ExitPromptError") {
        console.log(chalk.dim("\n  Exiting.\n"));
        return;
      }
      throw err;
    }
    },
  );
